import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  Typography,
  Grid,
  FormControlLabel,
  Switch,Stack,
} from '@mui/material';
import { subscriptionService } from '../../services/subscriptionService';

const SubscriptionPlanModal = ({ open, onClose, onSuccess, plan = null }) => {
  const [formData, setFormData] = useState({
    code: '',
    libelle: '',
    description: '',
    nombreProduit: 0,
    prix: 0,
    statut: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (plan) {
        // console.log('Plan data received for editing:', plan);
        const isFree = plan.code === 'FREE' || plan.code === 'FR';
        setFormData({
          code: plan.code || '',
          libelle: plan.libelle || '',
          description: plan.description || '',
          nombreProduit: plan.nombreProduit || 0,
          prix: plan.prix || 0,
          // La formule FREE doit toujours être active
          statut: isFree ? true : (plan.statut !== false)
        });
        // console.log('Form data set for editing:', {
        //   code: plan.code || '',
        //   libelle: plan.libelle || '',
        //   description: plan.description || '',
        //   nombreProduit: plan.nombreProduit || 0,
        //   prix: plan.prix || 0,
        //   statut: plan.statut !== false
        // });
      } else {
        setFormData({
          code: '',
          libelle: '',
          description: '',
          nombreProduit: 0,
          prix: 0,
          statut: true
        });
      }
      setError('');
    }
  }, [plan, open]);

  // Vérifier si c'est la formule FREE (formule par défaut obligatoire)
  const isFreeFormula = plan && (plan.code === 'FREE' || plan.code === 'FR');

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Empêcher la modification du code pour la formule FREE
    if (isFreeFormula && name === 'code') {
      return;
    }
    
    let processedValue = value;
    if (name === 'nombreProduit' || name === 'prix') {
      if (value === '') {
        processedValue = '';
      } else {
        const numValue = Number(value);
        processedValue = isNaN(numValue) ? 0 : numValue;
      }
    }
    
    setFormData(prev => {
      const newData = {
        ...prev,
        [name]: processedValue
      };
      // S'assurer que le statut reste true pour la formule FREE
      if (isFreeFormula) {
        newData.statut = true;
      }
      return newData;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
 
      if (plan) {
        await subscriptionService.updateSubscriptionPlan(plan.id, formData);
        onSuccess('Formule d\'abonnement modifiée avec succès');
      } else {
        await subscriptionService.createSubscriptionPlan(formData);
        onSuccess('Formule d\'abonnement créée avec succès');
      }
      onClose();
    } catch (err) {
      console.error('Error submitting form:', err);
      setError(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {plan
          ? "Modifier la formule d'abonnement"
          : "Créer une formule d'abonnement"}
      </DialogTitle>
  
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
  
          {/* Un champ par ligne */}
          <Stack spacing={2}>
            <TextField
              name="code"
              label="Code de la formule"
              value={formData.code}
              onChange={handleChange}
              required
              fullWidth
              disabled={isFreeFormula}
              helperText={
                isFreeFormula 
                  ? "Le code de la formule FREE ne peut pas être modifié (formule par défaut obligatoire)"
                  : "Code unique pour identifier la formule"
              }
            />
  
            <TextField
              name="libelle"
              label="Libellé"
              value={formData.libelle}
              onChange={handleChange}
              required
              fullWidth
              helperText="Nom de la formule d'abonnement"
            />
  
            <TextField
              name="description"
              label="Description"
              value={formData.description}
              onChange={handleChange}
              multiline
              rows={3}
              fullWidth
              helperText="Description détaillée de la formule"
            />
  
            <TextField
              name="nombreProduit"
              label="Nombre de produits autorisés"
              type="number"
              value={formData.nombreProduit}
              onChange={handleChange}
              required
              fullWidth
              inputProps={{ min: 0 }}
              helperText="Nombre maximum de produits que peut créer le vendeur"
            />
  
            <TextField
              name="prix"
              label="Prix de la formule (FCFA)"
              type="number"
              value={formData.prix}
              onChange={handleChange}
              required
              fullWidth
              inputProps={{ min: 0, step: 100 }}
              helperText="Prix de base de cette formule d'abonnement"
            />
  
            <FormControlLabel
              control={
                <Switch
                  checked={formData.statut}
                  onChange={(e) => {
                    // Empêcher la désactivation de la formule FREE
                    if (isFreeFormula) {
                      return;
                    }
                    setFormData((prev) => ({
                      ...prev,
                      statut: e.target.checked,
                    }));
                  }}
                  color="primary"
                  disabled={isFreeFormula}
                />
              }
              label={
                isFreeFormula 
                  ? "Formule active (obligatoire pour FREE)"
                  : "Formule active"
              }
            />
            {isFreeFormula && (
              <Alert severity="info" sx={{ mt: 1 }}>
                La formule FREE est la formule par défaut obligatoire. Elle doit toujours être active.
              </Alert>
            )}
          </Stack>
        </DialogContent>
  
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button type="submit" variant="contained" disabled={loading}>
            {loading ? "Sauvegarde..." : plan ? "Modifier" : "Créer"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
  
};

export default SubscriptionPlanModal;
